// Right-side slide-out drawer for creating a new account.
// Account name · Account type · Detail type · Make subaccount · Description · Lock account
// Cancel · Save · Save and new (split).

import React, { useEffect, useState } from "react";
import { X, Lock, Unlock, ChevronDown } from "lucide-react";
import type { AccountType } from "../types";
import { DETAIL_TYPES_BY_TYPE, TYPE_NUMBER_START } from "../constants";

interface NewAccountDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreate: (draft: { number: string; name: string; type: AccountType; detailType: string; description: string; locked: boolean; isSubaccount: boolean }) => void;
  suggestedName?: string;
  suggestedType?: AccountType;
}

const ACCOUNT_TYPES: AccountType[] = ["Asset", "Liability", "Equity", "Income", "Expense"];

export default function NewAccountDrawer({ open, onClose, onCreate, suggestedName, suggestedType }: NewAccountDrawerProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>("Expense");
  const [detailType, setDetailType] = useState("");
  const [description, setDescription] = useState("");
  const [isSubaccount, setIsSubaccount] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showTypeDd, setShowTypeDd] = useState(false);
  const [showDetailDd, setShowDetailDd] = useState(false);

  useEffect(() => {
    if (open) {
      setName(suggestedName ?? "");
      setType(suggestedType ?? "Expense");
      setDetailType("");
      setDescription("");
      setIsSubaccount(false);
      setLocked(false);
      setShowTypeDd(false);
      setShowDetailDd(false);
    }
  }, [open, suggestedName, suggestedType]);

  if (!open) return null;

  const submit = (keepOpen: boolean) => {
    if (!name.trim() || !detailType) return;
    const number = String(TYPE_NUMBER_START[type] + Math.floor(Math.random() * 900));
    onCreate({ number, name: name.trim(), type, detailType, description, locked, isSubaccount });
    if (keepOpen) {
      setName("");
      setDetailType("");
      setDescription("");
    } else {
      onClose();
    }
  };

  const detailOptions = DETAIL_TYPES_BY_TYPE[type];
  const canSave = name.trim().length > 0 && detailType.length > 0;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/40 z-[999] animate-[nadFade_0.25s_ease-out]" />
      <aside className="fixed top-0 right-0 bottom-0 w-full md:w-[460px] max-w-full bg-white z-[1000] shadow-[-8px_0_32px_rgba(14,26,26,0.15)] flex flex-col animate-[nadSlide_0.3s_ease-out]">
        <div className="px-5 md:px-6 py-4 border-b border-lineSoft flex justify-between items-center flex-shrink-0">
          <h2 className="text-base font-extrabold text-ink m-0">New account</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-lg bg-transparent border-none cursor-pointer text-ink3 flex items-center justify-center hover:bg-lineSoft">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 md:px-6 py-4">
          <FieldLabel>Account name <span className="text-red-600">*</span></FieldLabel>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vehicle expenses" className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[13px] text-ink placeholder:text-ink4 outline-none focus:border-ink3" />

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <FieldLabel>Account type <span className="text-red-600">*</span></FieldLabel>
              <DropdownSelect value={type} options={ACCOUNT_TYPES} open={showTypeDd} setOpen={setShowTypeDd} onSelect={(v) => { setType(v as AccountType); setDetailType(""); }} />
            </div>
            <div>
              <FieldLabel>Detail type <span className="text-red-600">*</span></FieldLabel>
              <DropdownSelect value={detailType || "Select"} options={detailOptions} open={showDetailDd} setOpen={setShowDetailDd} onSelect={setDetailType} placeholder />
            </div>
          </div>

          <label className="flex items-center gap-2.5 mt-4 text-[13px] text-ink2 cursor-pointer">
            <input type="checkbox" checked={isSubaccount} onChange={(e) => setIsSubaccount(e.target.checked)} className="w-4 h-4" />
            Make this a subaccount
          </label>

          <FieldLabel>Description</FieldLabel>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[13px] text-ink outline-none focus:border-ink3 resize-y font-sans" />

          <div className="flex items-center gap-2.5 mt-4">
            <span className="text-[13px] font-semibold text-ink">Lock account</span>
            <div className="inline-flex gap-1 border border-line rounded-lg p-0.5">
              <button type="button" onClick={() => setLocked(true)} className={`w-7 h-7 rounded flex items-center justify-center cursor-pointer border-none ${locked ? "bg-ink text-white" : "bg-transparent text-ink3"}`}><Lock size={13} /></button>
              <button type="button" onClick={() => setLocked(false)} className={`w-7 h-7 rounded flex items-center justify-center cursor-pointer border-none ${!locked ? "bg-ink text-white" : "bg-transparent text-ink3"}`}><Unlock size={13} /></button>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-4 border-t border-lineSoft flex justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="text-[13px] text-ink3 font-bold bg-transparent border-none px-3.5 py-2 cursor-pointer">Cancel</button>
          <SaveButton canSave={canSave} onSave={() => submit(false)} onSaveAndNew={() => submit(true)} />
        </div>

        <style>{`
          @keyframes nadFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes nadSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>
      </aside>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-[11px] font-bold text-ink3 uppercase tracking-wider mt-3 mb-1.5">{children}</label>;
}

function DropdownSelect({ value, options, open, setOpen, onSelect, placeholder }: { value: string; options: string[]; open: boolean; setOpen: (v: boolean) => void; onSelect: (v: string) => void; placeholder?: boolean }) {
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="w-full bg-white border border-line rounded-lg px-3 py-2 text-[13px] text-ink flex justify-between items-center cursor-pointer">
        <span className={placeholder && value === "Select" ? "text-ink4" : "text-ink"}>{value}</span>
        <ChevronDown size={13} className="text-ink3" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-line rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto py-1">
          {options.map((o) => (
            <button key={o} type="button" onClick={() => { onSelect(o); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-[12.5px] text-ink bg-transparent border-none cursor-pointer hover:bg-panel">
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SaveButton({ canSave, onSave, onSaveAndNew }: { canSave: boolean; onSave: () => void; onSaveAndNew: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div ref={ref} className="relative inline-flex">
      <button type="button" disabled={!canSave} onClick={onSave} className="text-[13px] text-white font-bold bg-teal-dark border-none px-4 py-2 rounded-l-lg cursor-pointer disabled:opacity-50">Save</button>
      <button type="button" disabled={!canSave} onClick={() => setOpen((v) => !v)} className="text-white bg-teal-dark border-0 border-l border-l-white/30 px-2 py-2 rounded-r-lg cursor-pointer disabled:opacity-50">
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-line rounded-lg shadow-lg z-50 overflow-hidden min-w-[160px]">
          <button type="button" onClick={() => { setOpen(false); onSaveAndNew(); }} className="w-full text-left px-3 py-2 text-[12.5px] text-ink bg-transparent border-none cursor-pointer hover:bg-panel">Save and new</button>
        </div>
      )}
    </div>
  );
}